import { useEffect, useState } from "react";
import {
  Plus, Trash2, Building2, Landmark, Save, Hash, CalendarRange, AlertTriangle,
  Image as ImageIcon,
} from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, PageTitle } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const cuentaVacia = { banco: "", tipo: "Cuenta corriente", numero: "", titular: "", moneda: "DOP", nota: "" };

export default function AdminConfiguracion() {
  const { reloadConfig } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [ncf, setNcf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imagenes, setImagenes] = useState({ logo: "", portada: "" });

  const refrescarImagenes = (datos) => setImagenes({
    logo: datos.tiene_logo ? `${API}/configuracion/imagen/logo?v=${Date.now()}` : "",
    portada: datos.tiene_portada ? `${API}/configuracion/imagen/portada?v=${Date.now()}` : "",
  });

  useEffect(() => {
    api.get("/configuracion").then((r) => { setCfg(r.data); refrescarImagenes(r.data); });
    api.get("/ncf/siguiente").then((r) => setNcf(r.data));
  }, []);

  const subirImagen = (tipo, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("La imagen no debe superar los 5 MB");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/configuracion/imagen", { tipo, nombre: file.name, base64: reader.result });
        setImagenes({ ...imagenes, [tipo]: reader.result });
        await reloadConfig();
        toast.success(tipo === "logo" ? "Logo actualizado" : "Foto de portada actualizada");
      } catch (err) {
        toast.error(apiError(err));
      }
    };
    reader.readAsDataURL(file);
  };

  const borrarImagen = async (tipo) => {
    try {
      await api.delete(`/configuracion/imagen/${tipo}`);
      setImagenes({ ...imagenes, [tipo]: "" });
      await reloadConfig();
      toast.success("Imagen eliminada");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const guardar = async () => {
    if (!cfg.nombre.trim()) return toast.error("Indique el nombre del centro");
    if (!cfg.rnc.trim()) return toast.error("Indique el RNC");
    if (!cfg.anio_lectivo.trim()) return toast.error("Indique el año lectivo");
    if (cfg.periodos.filter((p) => p.trim()).length === 0) {
      return toast.error("Defina al menos un periodo de clases");
    }
    setBusy(true);
    try {
      const { data } = await api.put("/configuracion", cfg);
      setCfg(data);
      const r = await api.get("/ncf/siguiente");
      setNcf(r.data);
      await reloadConfig();
      toast.success("Configuración guardada. Se aplicará a las facturas y comprobantes.");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  if (!cfg) return <Loading />;

  const setCuenta = (i, campo, valor) => {
    const cuentas = [...cfg.cuentas];
    cuentas[i] = { ...cuentas[i], [campo]: valor };
    setCfg({ ...cfg, cuentas });
  };

  const setPeriodo = (i, valor) => {
    const periodos = [...cfg.periodos];
    periodos[i] = valor;
    setCfg({ ...cfg, periodos });
  };

  const plantillas = [
    { nombre: "4 periodos", valores: ["Primer Periodo", "Segundo Periodo", "Tercer Periodo", "Cuarto Periodo"] },
    { nombre: "3 trimestres", valores: ["Primer Trimestre", "Segundo Trimestre", "Tercer Trimestre"] },
    { nombre: "2 semestres", valores: ["Primer Semestre", "Segundo Semestre"] },
  ];

  return (
    <div data-testid="admin-configuracion">
      <PageTitle title="Datos del colegio y cuentas"
                 subtitle="Estos datos aparecen en las facturas en PDF, los correos y el portal de padres.">
        <Button className="rounded-full" onClick={guardar} disabled={busy} data-testid="guardar-configuracion">
          <Save className="mr-2 h-4 w-4" /> {busy ? "Guardando…" : "Guardar cambios"}
        </Button>
      </PageTitle>

      <div className="grid gap-8 lg:grid-cols-12">
        <section className="lg:col-span-7 rounded-lg border border-border/60 bg-card p-6 sm:p-8">
          <div className="flex items-center gap-2 text-primary">
            <Building2 className="h-4 w-4" />
            <h2 className="font-display text-lg font-bold">Datos fiscales del centro</h2>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>Nombre del colegio</Label>
              <Input value={cfg.nombre} data-testid="cfg-nombre"
                     onChange={(e) => setCfg({ ...cfg, nombre: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>RNC</Label>
              <Input value={cfg.rnc} data-testid="cfg-rnc"
                     onChange={(e) => setCfg({ ...cfg, rnc: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input value={cfg.telefono || ""} data-testid="cfg-telefono"
                     onChange={(e) => setCfg({ ...cfg, telefono: e.target.value })} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Dirección</Label>
              <Input value={cfg.direccion} data-testid="cfg-direccion"
                     onChange={(e) => setCfg({ ...cfg, direccion: e.target.value })} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Correo de administración</Label>
              <Input value={cfg.email || ""} data-testid="cfg-email"
                     onChange={(e) => setCfg({ ...cfg, email: e.target.value })} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Correos para el aviso de inventario</Label>
              <Input value={cfg.correos_inventario || ""} data-testid="cfg-correos-inventario"
                     placeholder="compras@colegio.edu.do, tienda@colegio.edu.do"
                     onChange={(e) => setCfg({ ...cfg, correos_inventario: e.target.value })} />
              <p className="text-xs text-muted-foreground">
                Cada mañana a las 7:00 a.m. se avisa qué artículos están agotados o bajo el mínimo.
                La administración y la secretaría siempre lo reciben; aquí puede sumar otros correos
                separados por comas.
              </p>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Nota legal al pie de la factura</Label>
              <Textarea rows={3} value={cfg.mensaje_factura || ""} data-testid="cfg-mensaje"
                        onChange={(e) => setCfg({ ...cfg, mensaje_factura: e.target.value })} />
            </div>
          </div>

          <div className="mt-8 rounded-md border border-border bg-muted/30 p-5">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-primary" />
              <h3 className="font-display text-base font-semibold">Identidad visual</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              El logo aparece en el portal, el login y las facturas. La foto de portada se ve en la pantalla de acceso.
            </p>
            <div className="mt-4 grid gap-6 sm:grid-cols-2">
              {[["logo", "Logo del colegio"], ["portada", "Foto de portada"]].map(([tipo, etiqueta]) => (
                <div key={tipo} className="space-y-3">
                  <Label>{etiqueta}</Label>
                  <div className={`flex items-center justify-center overflow-hidden rounded-md border border-border bg-background ${
                    tipo === "logo" ? "h-28" : "h-28"}`}>
                    {imagenes[tipo] ? (
                      <img src={imagenes[tipo]} alt={etiqueta} data-testid={`preview-${tipo}`}
                           className={tipo === "logo" ? "h-full w-auto object-contain p-2" : "h-full w-full object-cover"} />
                    ) : (
                      <span className="text-xs text-muted-foreground">Sin imagen cargada</span>
                    )}
                  </div>
                  <Input type="file" accept="image/*" data-testid={`subir-${tipo}`}
                         onChange={(e) => subirImagen(tipo, e)} />
                  {imagenes[tipo] && (
                    <Button size="sm" variant="ghost" onClick={() => borrarImagen(tipo)}
                            data-testid={`borrar-${tipo}`}>
                      <Trash2 className="mr-1 h-3 w-3 text-destructive" /> Quitar imagen
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 rounded-md border border-border bg-muted/30 p-5">
            <div className="flex items-center gap-2">
              <CalendarRange className="h-4 w-4 text-primary" />
              <h3 className="font-display text-base font-semibold">Año lectivo y periodos de clases</h3>
            </div>
            <div className="mt-4 space-y-2">
              <Label>Año lectivo</Label>
              <Input value={cfg.anio_lectivo} data-testid="cfg-anio-lectivo" placeholder="2026-2027"
                     onChange={(e) => setCfg({ ...cfg, anio_lectivo: e.target.value })} />
              <p className="text-xs text-muted-foreground">
                Aparece en el encabezado del portal, los contratos y las facturas.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                Periodos de calificación
              </span>
              {plantillas.map((p) => (
                <Button key={p.nombre} size="sm" variant="outline" className="rounded-full text-xs"
                        data-testid={`plantilla-${p.valores.length}`}
                        onClick={() => setCfg({ ...cfg, periodos: p.valores })}>
                  {p.nombre}
                </Button>
              ))}
            </div>
            <div className="mt-3 space-y-2" data-testid="lista-periodos">
              {cfg.periodos.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Badge variant="outline" className="shrink-0 font-mono text-xs">{i + 1}</Badge>
                  <Input value={p} data-testid={`periodo-${i}`} onChange={(e) => setPeriodo(i, e.target.value)} />
                  <Button size="icon" variant="ghost" data-testid={`eliminar-periodo-${i}`}
                          onClick={() => setCfg({ ...cfg, periodos: cfg.periodos.filter((_, j) => j !== i) })}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-3 rounded-full" data-testid="agregar-periodo"
                    onClick={() => setCfg({ ...cfg, periodos: [...cfg.periodos, ""] })}>
              <Plus className="mr-2 h-3 w-3" /> Agregar periodo
            </Button>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Los docentes eligen entre estos periodos al publicar calificaciones y los padres los ven en el boletín.
            </p>
          </div>

          <div className="mt-8 rounded-md border border-border bg-muted/30 p-5">
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-primary" />
              <h3 className="font-display text-base font-semibold">Comprobantes fiscales (NCF)</h3>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Prefijo de la secuencia</Label>
                <Input value={cfg.ncf_prefijo} data-testid="cfg-ncf-prefijo"
                       onChange={(e) => setCfg({ ...cfg, ncf_prefijo: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Próximo NCF automático</Label>
                <Input value={ncf?.sugerido || ""} readOnly className="font-mono" data-testid="cfg-ncf-siguiente" />
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Al emitir una factura puede aceptar el NCF automático o escribirlo manualmente (por ejemplo, para usar
              una secuencia autorizada por la DGII).
            </p>
          </div>
          <div className="mt-8 rounded-md border border-border bg-muted/30 p-5"
               data-testid="cfg-recargo-mora">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <h3 className="font-display text-base font-semibold">Recargo por mora</h3>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              Puede marcar el recargo a mano al emitir o editar una factura. Si activa el
              automático, cada mañana el sistema avisa a las familias y aplica el recargo a las
              mensualidades vencidas (una sola vez por factura).
            </p>
            <label className="mt-4 flex items-start gap-3 text-sm">
              <input type="checkbox" className="mt-1 h-4 w-4" checked={!!cfg.recargo_mora_auto}
                     data-testid="cfg-mora-auto"
                     onChange={(e) => setCfg({ ...cfg, recargo_mora_auto: e.target.checked })} />
              <span>
                <span className="font-semibold">Aplicar el recargo automáticamente</span>
                <span className="block text-xs text-muted-foreground">
                  Solo a facturas de mensualidad pendientes que pasaron de vencidas.
                </span>
              </span>
            </label>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Recargo (%)</Label>
                <Input type="number" value={cfg.recargo_mora_pct ?? 5} data-testid="cfg-mora-pct"
                       onChange={(e) => setCfg({ ...cfg, recargo_mora_pct: Number(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label>Días de gracia</Label>
                <Input type="number" value={cfg.recargo_mora_dias_gracia ?? 0}
                       data-testid="cfg-mora-gracia"
                       onChange={(e) => setCfg({ ...cfg,
                                                 recargo_mora_dias_gracia: Number(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label>Avisar días antes</Label>
                <Input type="number" value={cfg.recargo_mora_aviso_dias ?? 3}
                       data-testid="cfg-mora-aviso"
                       onChange={(e) => setCfg({ ...cfg,
                                                 recargo_mora_aviso_dias: Number(e.target.value) })} />
              </div>
            </div>
          </div>
        </section>

        <section className="lg:col-span-5 rounded-lg border border-border/60 bg-card p-6 sm:p-8">
          <div className="flex items-center gap-2 text-primary">
            <Landmark className="h-4 w-4" />
            <h2 className="font-display text-lg font-bold">Cuentas para pagos</h2>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Visibles para los padres al momento de pagar por transferencia.
          </p>

          <div className="mt-6 space-y-2">
            <Label>Instrucciones de pago</Label>
            <Textarea rows={3} value={cfg.instrucciones_pago || ""} data-testid="cfg-instrucciones"
                      onChange={(e) => setCfg({ ...cfg, instrucciones_pago: e.target.value })} />
          </div>

          <div className="mt-6 space-y-4" data-testid="lista-cuentas">
            {cfg.cuentas.map((c, i) => (
              <div key={i} className="rounded-md border border-border/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <Badge variant="outline">Cuenta {i + 1}</Badge>
                  <Button size="icon" variant="ghost" data-testid={`eliminar-cuenta-${i}`}
                          onClick={() => setCfg({ ...cfg, cuentas: cfg.cuentas.filter((_, j) => j !== i) })}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <Input placeholder="Banco" value={c.banco} data-testid={`cuenta-banco-${i}`}
                         onChange={(e) => setCuenta(i, "banco", e.target.value)} />
                  <Input placeholder="Tipo de cuenta" value={c.tipo} data-testid={`cuenta-tipo-${i}`}
                         onChange={(e) => setCuenta(i, "tipo", e.target.value)} />
                  <Input placeholder="Número de cuenta" value={c.numero} data-testid={`cuenta-numero-${i}`}
                         onChange={(e) => setCuenta(i, "numero", e.target.value)} />
                  <Input placeholder="Titular" value={c.titular} data-testid={`cuenta-titular-${i}`}
                         onChange={(e) => setCuenta(i, "titular", e.target.value)} />
                  <Input placeholder="Moneda" value={c.moneda} data-testid={`cuenta-moneda-${i}`}
                         onChange={(e) => setCuenta(i, "moneda", e.target.value)} />
                  <Input placeholder="Nota (opcional)" value={c.nota || ""}
                         onChange={(e) => setCuenta(i, "nota", e.target.value)} />
                </div>
              </div>
            ))}
          </div>

          <Button variant="outline" className="mt-5 w-full rounded-full" data-testid="agregar-cuenta"
                  onClick={() => setCfg({ ...cfg, cuentas: [...cfg.cuentas, { ...cuentaVacia }] })}>
            <Plus className="mr-2 h-4 w-4" /> Agregar cuenta bancaria
          </Button>
        </section>
      </div>
    </div>
  );
}
